import { db } from './firebase';
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { shouldSkipEaProjectBell } from './eaProjectBellPolicy';
import { isArchitectRole } from './userRoles';

async function skipPortalBellForRecipient(userId: string, type: string): Promise<boolean> {
  const userSnap = await getDoc(doc(db, 'users', userId));
  const data = userSnap.data();
  if (isArchitectRole(data?.role)) return true;
  return shouldSkipEaProjectBell({
    designations: data?.designations,
    type,
  });
}

export async function notifyPortalAnnotationCreated(args: {
  portalId: string;
  dealId: string;
  releaseId: string;
  authorName: string;
  projectName?: string;
  notifyUserIds: string[];
}): Promise<void> {
  const { portalId, dealId, releaseId, authorName, projectName, notifyUserIds } = args;
  const title = projectName ? `Portal feedback — ${projectName}` : 'New portal drawing feedback';
  const message = `${authorName} left a comment on released drawings.`;

  for (const userId of notifyUserIds) {
    if (!userId) continue;
    try {
      if (await skipPortalBellForRecipient(userId, 'portal_annotation')) continue;
      await addDoc(collection(db, 'userNotifications'), {
        userId,
        type: 'portal_annotation',
        title,
        message,
        read: false,
        dealId,
        portalId,
        releaseId,
        link: `/client-portals/${portalId}`,
        createdAt: Timestamp.now(),
      });
    } catch (err) {
      console.warn('[notifyPortalAnnotationCreated] failed for', userId, err);
    }
  }
}

export async function notifyPortalDeliverableApproved(args: {
  portalId: string;
  dealId: string;
  deliverableType: 'plans' | 'model' | 'concept';
  approvedByName: string;
  projectName?: string;
  notifyUserIds: string[];
  releaseVersionIndex?: number;
  leadId?: string;
}): Promise<void> {
  const {
    portalId,
    dealId,
    deliverableType,
    approvedByName,
    projectName,
    notifyUserIds,
    releaseVersionIndex,
    leadId,
  } = args;
  const label =
    deliverableType === 'plans'
      ? 'engineered drawings'
      : deliverableType === 'concept'
        ? '2D concept elevations'
        : '3D model';
  const ver =
    typeof releaseVersionIndex === 'number' ? ` (v${releaseVersionIndex})` : '';
  const title = projectName ? `Portal approval — ${projectName}` : 'Client portal deliverable approved';
  const message = `${approvedByName} approved the ${label}${ver}.`;
  const portalStep = deliverableType === 'plans' ? '2d-plans' : '3d-model';
  // Staff client workspace — `/client-portals/:id` is not a CRM route.
  const link = leadId
    ? `/client/${leadId}?deal=${encodeURIComponent(dealId)}&step=${encodeURIComponent(portalStep)}`
    : undefined;

  for (const userId of notifyUserIds) {
    if (!userId) continue;
    try {
      if (await skipPortalBellForRecipient(userId, 'portal_deliverable_approval')) continue;
      await addDoc(collection(db, 'userNotifications'), {
        userId,
        type: 'portal_deliverable_approval',
        title,
        message,
        read: false,
        dealId,
        leadId: leadId || null,
        portalId,
        portalStep,
        ...(link ? { link } : {}),
        createdAt: Timestamp.now(),
      });
    } catch (err) {
      console.warn('[notifyPortalDeliverableApproved] failed for', userId, err);
    }
  }
}

export async function resolvePortalPmNotifyUserIds(dealId: string): Promise<string[]> {
  try {
    const dealSnap = await getDoc(doc(db, 'enhancedDeals', dealId));
    if (!dealSnap.exists()) return [];
    const data = dealSnap.data() as { assignedTo?: string; projectManagerId?: string; ownerId?: string };
    return Array.from(new Set([data.assignedTo, data.projectManagerId, data.ownerId].filter(Boolean) as string[]));
  } catch {
    return [];
  }
}
